import { useQuery } from '@tanstack/react-query';
import { budgetService } from '@services/budget';
import {
  BudgetPerspective,
  BudgetRowStatus,
  BudgetStatusFilter,
  GetPaginatedBudgetRequestsInput,
} from '@models/budget.model';
import { BUDGET_REQUEST_DETAILS_QUERY_KEY } from './useBudgetRequestDetails';

export const PAGINATED_BUDGET_REQUESTS_QUERY_KEY = 'paginated-budget-requests';

interface UsePaginatedBudgetRequestsParams {
  page: number;
  limit: number;
  perspective?: BudgetPerspective;
  statusFilter?: BudgetStatusFilter;
  /** Precise status filter. Empty means "no filter", never "match nothing". */
  statuses?: BudgetRowStatus[];
  /** Free-text match on task and project names. Debounce before passing it in. */
  search?: string;
  enabled?: boolean;
}

export const usePaginatedBudgetRequests = ({
  page,
  limit,
  perspective,
  statusFilter = 'ALL',
  statuses,
  search,
  enabled = true,
}: UsePaginatedBudgetRequestsParams) => {
  const normalizedSearch = search?.trim() || undefined;
  // Sorted so ['NEW','COUNTERED'] and ['COUNTERED','NEW'] share one cache entry.
  const normalizedStatuses = statuses?.length ? [...statuses].sort() : undefined;

  const input: GetPaginatedBudgetRequestsInput = {
    page,
    limit,
    perspective,
    statusFilter,
    statuses: normalizedStatuses,
    search: normalizedSearch,
  };

  return useQuery({
    queryKey: [
      PAGINATED_BUDGET_REQUESTS_QUERY_KEY,
      BUDGET_REQUEST_DETAILS_QUERY_KEY,
      page,
      limit,
      perspective,
      statusFilter,
      normalizedStatuses,
      normalizedSearch,
    ],
    queryFn: () =>
      budgetService.getPaginatedBudgetRequestsByOrganization(input),
    enabled,
    // Hold the previous page on screen while the next one loads, so paging and
    // typing in the search box never flash an empty table between results.
    placeholderData: (previous: any) => previous,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 403 Forbidden or other client errors (4xx)
      if (error?.response?.errors?.[0]?.extensions?.code === 'FORBIDDEN') {
        return false;
      }
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
